// /apps/patient/src/pages/prescriptions/PrescriptionsList.tsx

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { patientApiService } from '../../services/api';
import { PageHeader } from '../../components/layout/PageHeader';
import { 
  Pill, Calendar, ChevronRight, AlertCircle, 
  Building2, RefreshCw, Hash
} from 'lucide-react';
import { format } from 'date-fns';

interface Prescription {
  id: string;
  created_at: number;
  medication_prescribed?: {
    drug_description: string;
    sig?: {
      sig_text: string;
    };
    quantity?: {
      value: number;
      unit: string;
    };
    days_supply?: number;
    number_of_refills?: number;
    refills_remaining?: number;
    substitutions?: string;
    written_date?: {
      date?: string;
    }
  };
  pharmacy?: {
    business_name: string;
    address?: {
      line1: string;
      city: string;
      state: string;
      zip: string;
    };
    communication_numbers?: {
      primary_phone: string;
    };
  };
  prescriber?: {
    name: {
      first_name?: string;
      last_name?: string;
    }
    communication_numbers?: {
      primary_phone?: {
        number: string;
      };
    }
  };
  prescriber_name?: string;
  prescribed_date?: string;
  expiration_date?: string;
}

interface PaginationInfo {
  itemsTotal: number;
  pageTotal: number;
  curPage: number;
  perPage: number;
}

export function PrescriptionsList() {
  const navigate = useNavigate();
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [preferredPharmacy, setPreferredPharmacy] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    itemsTotal: 0,
    pageTotal: 1,
    curPage: 1,
    perPage: 20
  });
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch prescriptions
      const rxResponse = await patientApiService.getMyPrescriptions({ page: 1, perPage: 20 });
      
      // Handle paginated response
      const rxData = rxResponse.items || [];
      setPrescriptions(rxData);
      
      // Update pagination info
      setPagination({
        itemsTotal: rxResponse.itemsTotal || 0,
        pageTotal: rxResponse.pageTotal || 1,
        curPage: rxResponse.curPage || 1,
        perPage: rxResponse.perPage || 20
      });

      // Try to fetch preferred pharmacy
      try {
        const pharmacyData = await patientApiService.getMyPharmacy();
        setPreferredPharmacy(pharmacyData);
      } catch (err) {
      }
    } catch (err: any) {
      console.error('Error fetching prescriptions:', err);
      setError(err.message || 'Failed to load prescriptions');
    } finally {
      setLoading(false);
    }
  };

  const loadMorePrescriptions = async () => {
    if (pagination.curPage >= pagination.pageTotal) return;
    
    setLoadingMore(true);
    try {
      const nextPage = pagination.curPage + 1;
      const response = await patientApiService.getMyPrescriptions({ 
        page: nextPage, 
        perPage: pagination.perPage 
      });
      
      const newPrescriptions = response.items || [];
      setPrescriptions([...prescriptions, ...newPrescriptions]);
      
      // Update pagination
      setPagination({
        ...pagination,
        curPage: response.curPage || nextPage
      });
    } catch (err) {
      console.error('Error loading more prescriptions:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  // Helper function to parse date strings correctly
  const parseDate = (dateValue: string | number | undefined): Date | null => {
    if (!dateValue) return null;
    
    // If it's a Unix timestamp (number)
    if (typeof dateValue === 'number') {
      return new Date(dateValue * 1000);
    }
    
    // If it's a YYYY-MM-DD string, parse it as local date (not UTC)
    if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateValue.split('-').map(Number);
      return new Date(year, month - 1, day); // month is 0-indexed
    }
    
    // Otherwise, try normal parsing
    return new Date(dateValue);
  };

  const PrescriptionCard = ({ prescription }: { prescription: Prescription }) => {
    const prescribedDateValue = prescription.prescribed_date || 
                               prescription.medication_prescribed?.written_date?.date || 
                               prescription.created_at;
    
    const prescribedDate = parseDate(prescribedDateValue);
    
    return (
      <div
        onClick={() => navigate(`/prescriptions/${prescription.id}`)}
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Pill className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">
                  {prescription.medication_prescribed?.drug_description || 'Unknown Medication'}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {prescription.medication_prescribed?.sig?.sig_text || 'No directions available'}
                </p>
                
                <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-500">
                  {prescription.medication_prescribed?.quantity && (
                    <span className="flex items-center gap-1">
                      <Hash className="h-3 w-3" />
                      Qty: {prescription.medication_prescribed.quantity.value} {prescription.medication_prescribed.quantity.unit}
                    </span>
                  )}
                  {prescription.medication_prescribed?.days_supply && (
                    <span>Days Supply: {prescription.medication_prescribed.days_supply}</span>
                  )}
                  {prescription.medication_prescribed?.number_of_refills !== undefined && (
                    <span className="flex items-center gap-1">
                      <RefreshCw className="h-3 w-3" />
                      Refills: {prescription.medication_prescribed.number_of_refills}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-4 mt-3">
                  {prescribedDate && (
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Prescribed {format(prescribedDate, 'MMM d, yyyy')}
                    </span>
                  )}
                  
                  {prescription.prescriber && (
                    <span className="text-xs text-gray-500">
                      Dr. {prescription.prescriber.name.first_name} {prescription.prescriber.name.last_name}
                    </span>
                  )}
                </div>

                {prescription.pharmacy && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-sm text-gray-600 flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      {prescription.pharmacy.business_name}
                      {prescription.pharmacy.address && (
                        <span className="text-xs text-gray-500">
                          • {prescription.pharmacy.address.city}, {prescription.pharmacy.address.state}
                        </span>
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader title="Prescription History" />
        <div className="mt-8 text-center">
          <div className="animate-pulse">
            <div className="h-32 bg-gray-200 rounded-lg mb-4"></div>
            <div className="h-32 bg-gray-200 rounded-lg mb-4"></div>
            <div className="h-32 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader title="Prescription History" />
        <div className="mt-8 bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
          <p className="text-red-700">{error}</p>
          <button
            onClick={fetchInitialData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader title="Prescription History" />

      {/* Preferred Pharmacy */}
      {preferredPharmacy && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Building2 className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900">Preferred Pharmacy</h3>
              <p className="text-sm text-blue-800 mt-1">{preferredPharmacy.business_name}</p>
              {preferredPharmacy.address && (
                <p className="text-sm text-blue-700 mt-1">
                  {preferredPharmacy.address.line1}, {preferredPharmacy.address.city}, {preferredPharmacy.address.state} {preferredPharmacy.address.zip}
                </p>
              )}
              {preferredPharmacy.communication_numbers?.primary_phone && (
                <p className="text-sm text-blue-700 mt-1">
                  Phone: {preferredPharmacy.communication_numbers.primary_phone}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="mt-6 bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Total Prescriptions</p>
            <p className="text-2xl font-semibold text-gray-900">{pagination.itemsTotal}</p>
          </div>
          {prescriptions.length > 0 && prescriptions[0] && (
            <div className="text-right">
              <p className="text-sm text-gray-600">Most Recent</p>
              <p className="text-sm font-medium text-gray-900">
                {(() => {
                  const dateValue = prescriptions[0].prescribed_date || 
                                   prescriptions[0].medication_prescribed?.written_date?.date || 
                                   prescriptions[0].created_at;
                  const date = parseDate(dateValue);
                  return date ? format(date, 'MMM d, yyyy') : 'Unknown';
                })()}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Prescriptions List */}
      <div className="mt-6 space-y-4">
        {prescriptions.length > 0 ? (
          <>
            {prescriptions.map((prescription) => (
              <PrescriptionCard key={prescription.id} prescription={prescription} />
            ))}
            
            {/* Load More Button */}
            {pagination.curPage < pagination.pageTotal && (
              <div className="text-center pt-4">
                <button
                  onClick={loadMorePrescriptions}
                  disabled={loadingMore}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12 bg-white rounded-lg">
            <Pill className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No prescriptions found</p>
          </div>
        )}
      </div>
    </div>
  );
}